import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import { addMonths } from 'date-fns/addMonths'
import { addWeeks } from 'date-fns/addWeeks'
import { format } from 'date-fns/format'
import { subMonths } from 'date-fns/subMonths'
import { subWeeks } from 'date-fns/subWeeks'
import { ChevronLeft, ChevronRight, Keyboard } from 'lucide-react'
import { useCallback, useRef, useState } from 'react'
import { Button } from 'components/Button'
import { Tabs, TabsList, TabsTrigger } from 'components/Tabs'
import { useAnalytics } from 'hooks'
import { useCalendarStore } from '../store/calendar-store'
import type { CalendarView } from '../types'
import { MiniCalendar } from './MiniCalendar'

interface CalendarHeaderProps {
  currentDate: Date
  view: CalendarView
  onDateChange: (date: Date) => void
  onViewChange: (view: CalendarView) => void
  hideViewToggle?: boolean
  onShowShortcuts?: () => void
}

export function CalendarHeader({
  currentDate,
  view,
  onDateChange,
  onViewChange,
  hideViewToggle,
  onShowShortcuts,
}: CalendarHeaderProps) {
  const { track } = useAnalytics()
  const entries = useCalendarStore(s => s.entries)
  const [showMiniCal, setShowMiniCal] = useState(false)
  const miniCalRef = useRef<HTMLDivElement>(null)

  const title =
    view === 'month'
      ? format(currentDate, 'MMMM yyyy')
      : `Week of ${format(currentDate, 'MMM d, yyyy')}`

  const handlePrev = useCallback(() => {
    track(AnalyticsEvents.Calendar.CALENDAR_DATE_NAVIGATED, { direction: 'prev' })
    onDateChange(view === 'month' ? subMonths(currentDate, 1) : subWeeks(currentDate, 1))
  }, [currentDate, view, onDateChange, track])

  const handleNext = useCallback(() => {
    track(AnalyticsEvents.Calendar.CALENDAR_DATE_NAVIGATED, { direction: 'next' })
    onDateChange(view === 'month' ? addMonths(currentDate, 1) : addWeeks(currentDate, 1))
  }, [currentDate, view, onDateChange, track])

  const handleToday = useCallback(() => {
    track(AnalyticsEvents.Calendar.CALENDAR_DATE_NAVIGATED, { direction: 'today' })
    onDateChange(new Date())
  }, [onDateChange, track])

  const handleMiniCalSelect = useCallback(
    (date: Date) => {
      onDateChange(date)
      onViewChange('week')
      setShowMiniCal(false)
    },
    [onDateChange, onViewChange]
  )

  return (
    <div className="flex items-center justify-between border-b px-4 py-3 sm:px-6">
      <div className="flex items-center gap-3">
        <div className="relative" ref={miniCalRef}>
          <button
            onClick={() => setShowMiniCal(prev => !prev)}
            className="t-h3 text-paper hover:text-acid"
            title="Open mini calendar"
          >
            {title}
          </button>
          {showMiniCal && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowMiniCal(false)} />
              <div className="absolute top-full left-0 z-50 mt-2 w-[220px] rounded-[10px] border bg-bg-2 p-3 shadow-lg">
                <MiniCalendar
                  entries={entries}
                  currentDate={currentDate}
                  onDateSelect={handleMiniCalSelect}
                />
              </div>
            </>
          )}
        </div>
        <div className="flex items-center gap-1">
          {(
            [
              ['Previous', handlePrev, ChevronLeft],
              ['Next', handleNext, ChevronRight],
            ] as const
          ).map(([label, onClick, Icon]) => (
            <Button
              key={label}
              variant="t3-ghost"
              size="t3-sm"
              onClick={onClick}
              aria-label={label}
            >
              <Icon size={18} />
            </Button>
          ))}
        </div>
        <Button variant="t3-tertiary" size="t3-sm" onClick={handleToday}>
          Today
        </Button>
      </div>

      <div className="flex items-center gap-2">
        {onShowShortcuts && (
          <Button
            variant="t3-ghost"
            size="t3-sm"
            onClick={onShowShortcuts}
            title="Keyboard shortcuts (?)"
            aria-label="Keyboard shortcuts"
          >
            <Keyboard size={16} />
          </Button>
        )}

        {!hideViewToggle && (
          <Tabs value={view} onValueChange={v => onViewChange(v as CalendarView)}>
            <TabsList variant="pill">
              <TabsTrigger value="week" variant="pill">
                Week
              </TabsTrigger>
              <TabsTrigger value="month" variant="pill">
                Month
              </TabsTrigger>
            </TabsList>
          </Tabs>
        )}
      </div>
    </div>
  )
}
